import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Spin, Slider, Tooltip } from "antd";
import { LeftOutlined, RightOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/5.2.133/pdf.worker.min.js`;

const DocumentPreview = ({ document, visible, onClose }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const previousZoomRef = useRef(1.0);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1.0);

  useEffect(() => {
    if (visible && document?.file_url && document?.file_type === "application/pdf") {
      loadPdf(document.file_url);
    }
  }, [visible, document]);

  const loadPdf = async (url) => {
    try {
      setLoading(true);
      const loadingTask = pdfjsLib.getDocument(url);
      const pdf = await loadingTask.promise;
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setPageIndex(0);
      setZoom(1.0);
      previousZoomRef.current = 1.0;
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
        containerRef.current.scrollLeft = 0;
      }
    } catch (error) {
      console.error("Error loading PDF:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pdfDoc && canvasRef.current) {
      renderPage();
      // Reset scroll position when page changes
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
        containerRef.current.scrollLeft = 0;
      }
    }
  }, [pdfDoc, pageIndex]);

  useEffect(() => {
    if (pdfDoc && canvasRef.current && containerRef.current) {
      const container = containerRef.current;
      const oldZoom = previousZoomRef.current;
      
      // Only adjust scroll if zoom actually changed (not initial render)
      if (oldZoom !== zoom && oldZoom > 0) {
        // Save current scroll position before zoom change
        const savedScrollTop = container.scrollTop;
        const savedScrollLeft = container.scrollLeft;
        
        const adjustScroll = async () => {
          await renderPage();
          // After rendering with new zoom, adjust scroll position proportionally
          const zoomRatio = zoom / oldZoom;
          container.scrollTop = savedScrollTop * zoomRatio;
          container.scrollLeft = savedScrollLeft * zoomRatio;
          previousZoomRef.current = zoom;
        };
        
        adjustScroll();
      } else {
        // Initial render or zoom hasn't changed, just render
        renderPage();
        previousZoomRef.current = zoom;
      }
    }
  }, [zoom]);

  const renderPage = async () => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(pageIndex + 1);
      const viewport = page.getViewport({ scale: zoom });
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;
    } catch (error) {
      console.error("Error rendering page:", error);
    }
  };

  const handlePrevious = () => {
    if (pageIndex > 0) {
      setPageIndex(pageIndex - 1);
    }
  };

  const handleNext = () => {
    if (pageIndex < numPages - 1) {
      setPageIndex(pageIndex + 1);
    }
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  if (!document || document.file_type !== "application/pdf") {
    return null;
  }

  return (
    <Modal
      title={document.name}
      open={visible}
      onCancel={onClose}
      footer={null}
      width="90%"
      style={{ top: 20 }}
      bodyStyle={{ padding: 0, height: "80vh", display: "flex", flexDirection: "column" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", borderBottom: "1px solid #f0f0f0" }}>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Button icon={<LeftOutlined />} onClick={handlePrevious} disabled={pageIndex === 0}>
            Previous
          </Button>
          <span>
            Page {pageIndex + 1} of {numPages}
          </span>
          <Button icon={<RightOutlined />} onClick={handleNext} disabled={pageIndex === numPages - 1}>
            Next
          </Button>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", minWidth: "260px" }}>
          <Tooltip title="Zoom out">
            <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} disabled={zoom <= 0.5} aria-label="Zoom out" />
          </Tooltip>
          <Slider
            min={50}
            max={300}
            step={25}
            value={Math.round(zoom * 100)}
            onChange={(value) => setZoom(value / 100)}
            tooltip={{ formatter: (value) => `${value}%` }}
            style={{ flex: 1 }}
          />
          <Tooltip title="Zoom in">
            <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} disabled={zoom >= 3.0} aria-label="Zoom in" />
          </Tooltip>
        </div>
      </div>
      <div 
        ref={containerRef}
        style={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "20px", backgroundColor: "#f5f5f5" }}
      >
        <Spin spinning={loading}>
          <canvas ref={canvasRef} style={{ maxWidth: "100%", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }} />
        </Spin>
      </div>
    </Modal>
  );
};

export default DocumentPreview;
